import type { VfMenuItem } from "@/composables/useMenuBuilder"
import { useDelayedAction, type DelayedAction } from "@/vf"
import { defineStore } from "pinia"
import { ref } from "vue"

export const useNavbarDropdownState = defineStore("navbar-dropdown-state", () => {
    // refs for inspection
    const autoCloseMs = ref(500)
    const timers = ref(new Map<number, Map<string, DelayedAction>>())

    function hover(level: number, item: VfMenuItem, openFn?: () => void, closeFn?: () => void) {
        // initialize the level
        if (!timers.value.has(level)) {
            timers.value.set(level, new Map())
        }

        // create timer
        if (!timers.value.get(level)!.has(item.id)) {
            timers.value.get(level)!.set(
                item.id,
                useDelayedAction(autoCloseMs.value, () => {
                    closeFn?.()

                    // when menu is closed, the timer is no longer needed.
                    // this frees up some memory and prevents issues when the same menu is opened again
                    // and the closeFn is changed (e.g. when the menu component was unloaded and then reinstantiated)
                    timers.value.get(level)!.delete(item.id)
                }),
            )
        }

        // close all other submenus on the same level
        for (const [, value] of timers.value.get(level)!) {
            if (value === timers.value.get(level)!.get(item.id)) {
                continue
            }

            // stop timer
            value.stop()

            // close sub menu immediately
            value.action()
        }

        // stop the timer for the hovered item
        timers.value.get(level)!.get(item.id)?.stop()

        // if not already open, open the submenu
        openFn?.()
    }

    function leave(level: number, item: VfMenuItem) {
        timers.value.get(level)?.get(item.id)?.start()
    }

    function submenuOnClose(level: number, item: VfMenuItem) {
        timers.value.get(level)?.get(item.id)?.stop()
    }

    return {
        hover,
        leave,
        submenuOnClose,

        // export for inspection
        autoCloseMs,
        timers,
    }
})
